import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { LifeStageDateStatusEnum } from "@/Common/Models/LifeStageDateStatusEnum";
import { CareComponent } from "../Models/CareElements/CareComponent";
import { CareElement } from "../Models/CareElements/CareElement";
import { CareElementType } from "../Models/CareElements/CareElementType";

interface CareComponentElement {
    CareComponentName: string;
    CareElementName: string;
}

export class FlowsheetCareComponentValidatorHelper {
    public static isFlowsheetValid(careComponentData: CareComponent[]): boolean {
        if (!careComponentData || careComponentData.length === 0)
            return true;

        for (let careComponent of careComponentData) {
            if (!careComponent.CareElements || careComponent.CareElements.length === 0)
                continue;

            for (let careElement of careComponent.CareElements) {
                if (!this.handleCareElement(careElement)) {
                    return false;
                }
            }
        }
        return true;
    }

    public static readonly ExpectedCareComponentElements: CareComponentElement[] = [
        ...[
            "SmokingCessationTopic",
            "NutritionTopic",
            "ExerciseTopic",
            "ImmunizationTopic",
            "MedicationTopic"
        ].map(topic => ({ CareComponentName: "CounselingAndEducationCareComponent", CareElementName: topic })),
        ...[
            "EyeExam",
            "FootExam",
            "NeurologicalExam"
        ].map(exam => ({ CareComponentName: "ScreeningSectionCareComponent", CareElementName: exam }))
    ];

    public static getInitialFieldValidityMap(components: CareComponent[]): Record<string, boolean> {
        const map: Record<string, boolean> = {};

        this.ExpectedCareComponentElements.forEach(expected => {
            const component = components.find(c => c.Name === expected.CareComponentName);
            const element = component?.CareElements?.find(e => e.Name === expected.CareElementName);
            const key = expected.CareElementName;

            map[key] = this.handleCareElement(element);
        });

        return map;
    }

    public static handleCareElement(element: CareElement): boolean {
        if (!element)
            return true;

        switch (element.CareElementType) {
            case CareElementType.Subject:
                return this.validateDateField(element.DateReviewed);
            case CareElementType.RiskFactor:
                return this.validateDateField(element.ReportingPeriod?.StartDate);
            case CareElementType.BooleanReviewed:
            case CareElementType.BooleanTextReviewed:
            case CareElementType.DecimalReviewed:
            case CareElementType.IntegerReviewed:
            case CareElementType.TextReviewed:
            case CareElementType.MultiSelectReviewed:
            case CareElementType.FirstLineReviewed:
            case CareElementType.AgentDoseReviewed:
                return this.validateDateField(element.DateReviewedWithPrecision);
            case CareElementType.DateOnlyReviewed:
                return this.validateDateField(element.DateReviewedWithPrecision) && this.validateDateField(element.ValueWithPrecision);
            default:
                return true;
        }
    }

    public static validateDateField(date: string): boolean;
    public static validateDateField(date: DateTimeWithPrecision): boolean;
    public static validateDateField(date: Date): boolean;
    public static validateDateField(date: Date | DateTimeWithPrecision | string): boolean {
        if (!date)
            return true;

        let datePrecision: DateTimeWithPrecision;

        if (date instanceof DateTimeWithPrecision)
            datePrecision = date;
        else if (typeof date === "string")
            datePrecision = new DateTimeWithPrecision(date, "", LifeStageDatePrecisionEnum.Day);
        else
            datePrecision = DateTimeWithPrecision.getDate(date);

        return (
            IsNullOrWhiteSpace(datePrecision.text) ||
            (datePrecision.status === LifeStageDateStatusEnum.Valid && datePrecision.isDayPrecision)
        );
    }
}
